/* The cart, as one custom element.
 *
 * Rules this file enforces:
 *   - Liquid owns the markup. Every mutation asks Shopify to re-render the
 *     ak-cart-drawer section and swaps it in whole. No cart HTML is built
 *     here, and every money string shown was formatted server-side.
 *   - One request per mutation: /cart/*.js is called with a `sections`
 *     param so the fresh drawer rides back on the mutation response.
 *   - Adding does not interrupt: a popover confirms by the cart icon and
 *     dismisses itself. The drawer is something the customer opens.
 *   - Everything degrades: without this file, cart links land on /cart and
 *     the drawer's checkout is a real form.
 *
 * Public surface:
 *   document.querySelector('ak-cart-drawer').add(variantId, qty)
 *   document.querySelector('ak-cart-drawer').open()
 *   event 'ak:cart:changed' (detail.itemCount) for the header pill.
 */
(function () {
  'use strict';

  var SECTION = 'ak-cart-drawer';
  var POP_DWELL_MS = 4600;

  if (customElements.get('ak-cart-drawer')) return;

  var focusableSel = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var reducedMotion = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  customElements.define('ak-cart-drawer', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound) return;
      this.dataset.bound = '1';
      this.returnFocusTo = null;
      this.popTimer = null;
      this.bind();
    }

    /* ---- wiring ----------------------------------------------------- */

    bind() {
      var self = this;

      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-ak-drawer-close]') || e.target === self.querySelector('[data-ak-drawer-scrim]')) {
          if (!e.target.closest('a[href]')) e.preventDefault();
          self.close();
          return;
        }

        if (e.target.closest('[data-ak-pop-view]')) {
          self.hidePop();
          self.open();
          return;
        }

        if (e.target.closest('[data-ak-pop-checkout]')) {
          var form = self.querySelector('#ak-cart-form');
          if (form) form.submit();
          return;
        }

        var step = e.target.closest('[data-ak-qty-change]');
        if (step) {
          var input = self.querySelector('[data-ak-qty-input][data-line="' + step.dataset.line + '"]');
          var next = Math.max(0, (parseInt(input && input.value, 10) || 0) + Number(step.dataset.akQtyChange));
          self.changeLine(step.dataset.line, next);
          return;
        }

        var remove = e.target.closest('[data-ak-line-remove]');
        if (remove) self.changeLine(remove.dataset.line, 0);
      });

      this.addEventListener('change', function (e) {
        var input = e.target.closest('[data-ak-qty-input]');
        if (input) {
          self.changeLine(input.dataset.line, Math.max(0, parseInt(input.value, 10) || 0));
        }
      });

      // The popover holds its ground while pointed at, then leaves quickly.
      this.addEventListener('mouseenter', function (e) {
        if (e.target === self.querySelector('[data-ak-cart-pop]')) self.holdPop();
      }, true);
      this.addEventListener('mouseleave', function (e) {
        if (e.target === self.querySelector('[data-ak-cart-pop]')) self.startPopTimer(1600);
      }, true);
      this.addEventListener('focusin', function (e) {
        if (e.target.closest('[data-ak-cart-pop]')) self.holdPop();
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          if (self.hasAttribute('open')) self.close();
          self.hidePop();
        } else if (e.key === 'Tab' && self.hasAttribute('open')) {
          self.trapFocus(e);
        }
      });

      document.addEventListener('click', function (e) {
        // Outside click dismisses the popover early.
        var pop = self.querySelector('[data-ak-cart-pop]');
        if (pop && !pop.hidden && !pop.contains(e.target) && !e.target.closest('[data-ak-cart-open]')) {
          self.hidePop();
        }

        var opener = e.target.closest('[data-ak-cart-open]');
        if (!opener) return;
        e.preventDefault();
        self.hidePop();
        self.returnFocusTo = opener;
        self.open();
      });

      document.addEventListener('submit', function (e) {
        var form = e.target.closest('form[action*="/cart/add"]');
        if (!form) return;
        e.preventDefault();
        self.flyFrom(form);
        self.addFormData(new FormData(form), form.querySelector('[type="submit"]'));
      });
    }

    /* ---- drawer mechanics -------------------------------------------- */

    open() {
      this.setAttribute('open', '');
      document.documentElement.classList.add('ak-locked');
      var close = this.querySelector('[data-ak-drawer-close]');
      if (close) close.focus();
    }

    close() {
      this.removeAttribute('open');
      document.documentElement.classList.remove('ak-locked');
      if (this.returnFocusTo && document.contains(this.returnFocusTo)) {
        this.returnFocusTo.focus();
      }
    }

    trapFocus(e) {
      var panel = this.querySelector('[data-ak-drawer-panel]');
      var nodes = panel ? panel.querySelectorAll(focusableSel) : [];
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ---- added-to-cart popover ---------------------------------------
       A status, not a modal: positioned against the cart icon, never takes
       focus, dismisses itself. */

    showPop() {
      var pop = this.querySelector('[data-ak-cart-pop]');
      var anchor = document.querySelector('[data-ak-cart-open]');
      if (!pop || !anchor) return;

      var count = Number(this.dataset.itemCount) || 0;
      pop.querySelector('[data-ak-pop-count]').textContent =
        count + (count === 1 ? ' item' : ' items') + ' in cart';
      pop.querySelector('[data-ak-pop-total]').textContent = this.dataset.subtotal || '';

      // The header may be tucked away mid-scroll; it pins itself visible on
      // this event so the popover has its anchor on screen. Re-place once
      // the reveal transition lands, since the anchor moves with it.
      document.dispatchEvent(new CustomEvent('ak:cart:pop-open'));
      this.placePop(pop, anchor);
      window.setTimeout(this.placePop.bind(this, pop, anchor), 340);
      pop.hidden = false;
      pop.classList.remove('is-entering');
      void pop.offsetWidth; /* restart the entrance animation */
      pop.classList.add('is-entering');

      var self = this;
      this.repositionPop = function () {
        if (!pop.hidden) self.placePop(pop, anchor);
      };
      window.addEventListener('resize', this.repositionPop, { passive: true });
      window.addEventListener('scroll', this.repositionPop, { passive: true });

      this.startPopTimer(POP_DWELL_MS);
    }

    placePop(pop, anchor) {
      var rect = anchor.getBoundingClientRect();
      pop.style.top = Math.round(rect.bottom + 10) + 'px';
      pop.style.right = Math.round(Math.max(8, window.innerWidth - rect.right - 8)) + 'px';
    }

    hidePop() {
      var pop = this.querySelector('[data-ak-cart-pop]');
      var wasVisible = pop && !pop.hidden;
      if (pop) pop.hidden = true;
      this.holdPop();
      if (this.repositionPop) {
        window.removeEventListener('resize', this.repositionPop);
        window.removeEventListener('scroll', this.repositionPop);
        this.repositionPop = null;
      }
      if (wasVisible) {
        document.dispatchEvent(new CustomEvent('ak:cart:pop-close'));
      }
    }

    holdPop() {
      if (this.popTimer) {
        window.clearTimeout(this.popTimer);
        this.popTimer = null;
      }
    }

    startPopTimer(ms) {
      var self = this;
      this.holdPop();
      this.popTimer = window.setTimeout(function () { self.hidePop(); }, ms);
    }

    /* ---- fly-to-cart --------------------------------------------------
       The product image leaves its card and lands on the cart icon. Purely
       cosmetic: starts on submit for immediacy, and skipped entirely under
       reduced motion. */

    flyFrom(form) {
      if (reducedMotion || !('animate' in Element.prototype)) return;

      var card = form.closest('[data-ak-product]') || form;
      /* "the first image in the card" is wrong the moment a card holds any
         decoration: on the product page the hero's decorative phyto decal is
         rendered before the product, so the thing that flew to the cart was
         a background flourish. Ask for the product image by name, and fall
         back to the first image that is not marked decorative. */
      var img = card.querySelector('[data-ak-fly]') ||
                card.querySelector('img:not([aria-hidden="true"])') ||
                card.querySelector('img');
      var target = document.querySelector('[data-ak-cart-open]');
      if (!img || !target) return;

      var from = img.getBoundingClientRect();
      var to = target.getBoundingClientRect();
      if (!from.width || !to.width) return;

      var clone = img.cloneNode(false);
      clone.className = 'ak-fly';
      clone.style.top = from.top + 'px';
      clone.style.left = from.left + 'px';
      clone.style.width = from.width + 'px';
      clone.style.height = from.height + 'px';
      document.body.appendChild(clone);

      var dx = (to.left + to.width / 2) - (from.left + from.width / 2);
      var dy = (to.top + to.height / 2) - (from.top + from.height / 2);

      clone.animate([
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: 'translate(' + dx * 0.4 + 'px, ' + (dy * 0.4 - 36) + 'px) scale(0.55)', opacity: 0.9, offset: 0.45 },
        { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(0.08)', opacity: 0.25 }
      ], {
        duration: 620,
        easing: 'cubic-bezier(0.5, 0.05, 0.75, 0.4)'
      }).onfinish = function () {
        clone.remove();
      };
    }

    /* ---- mutations ---------------------------------------------------- */

    setBusy(busy) {
      var body = this.querySelector('[data-ak-drawer-body]');
      if (body) body.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    showError(message) {
      var el = this.querySelector('[data-ak-drawer-error]');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    add(variantId, quantity) {
      var fd = new FormData();
      fd.append('id', String(variantId));
      fd.append('quantity', String(quantity || 1));
      return this.addFormData(fd);
    }

    addFormData(fd, submitButton) {
      fd.append('sections', SECTION);
      return this.postAdd({ body: fd }, submitButton);
    }

    /* Several lines in one request. Used when the product page offers an
       upgrade alongside the main item: one POST, one cart render, and the
       two lines cannot half-succeed the way two sequential adds can. */
    addMany(items, submitButton) {
      return this.postAdd({
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items, sections: SECTION })
      }, submitButton);
    }

    postAdd(init, submitButton) {
      var self = this;
      if (submitButton) submitButton.setAttribute('aria-disabled', 'true');
      self.setBusy(true);

      var headers = { Accept: 'application/json' };
      if (init.headers) {
        Object.keys(init.headers).forEach(function (k) { headers[k] = init.headers[k]; });
      }

      return fetch(window.AKRoutes.cartAdd + '.js', {
        method: 'POST',
        headers: headers,
        body: init.body
      })
        .then(function (res) { return res.json().then(function (json) { return { ok: res.ok, json: json }; }); })
        .then(function (r) {
          if (!r.ok) {
            // Stock limits and the like: the drawer carries the explanation.
            self.open();
            self.showError((r.json && (r.json.description || r.json.message)) || 'That could not be added to the cart.');
            return null;
          }
          self.renderFrom(r.json.sections);
          self.showError('');
          self.showPop();
          return r.json;
        })
        .catch(function () {
          self.showError('Something went wrong updating the cart. The cart page still works.');
        })
        .finally(function () {
          self.setBusy(false);
          if (submitButton) submitButton.removeAttribute('aria-disabled');
        });
    }

    changeLine(line, quantity) {
      var self = this;
      self.setBusy(true);
      return fetch(window.AKRoutes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: quantity, sections: SECTION })
      })
        .then(function (res) { return res.json().then(function (json) { return { ok: res.ok, json: json }; }); })
        .then(function (r) {
          if (!r.ok) {
            self.showError((r.json && (r.json.description || r.json.message)) || 'The cart could not be updated.');
            return null;
          }
          self.renderFrom(r.json.sections);
          return r.json;
        })
        .catch(function () {
          self.showError('Something went wrong updating the cart. The cart page still works.');
        })
        .finally(function () {
          self.setBusy(false);
        });
    }

    /* ---- section swap ------------------------------------------------- */

    renderFrom(sections) {
      if (!sections || !sections[SECTION]) return;
      var doc = new DOMParser().parseFromString(sections[SECTION], 'text/html');
      var fresh = doc.querySelector('ak-cart-drawer');
      if (!fresh) return;

      var wasOpen = this.hasAttribute('open');
      this.hidePop(); /* the popover's listeners point at replaced nodes */
      this.innerHTML = fresh.innerHTML;
      this.dataset.itemCount = fresh.dataset.itemCount || '0';
      this.dataset.subtotal = fresh.dataset.subtotal || '';
      if (wasOpen) {
        var close = this.querySelector('[data-ak-drawer-close]');
        if (close) close.focus();
      }

      document.dispatchEvent(new CustomEvent('ak:cart:changed', {
        detail: { itemCount: Number(this.dataset.itemCount) || 0 }
      }));
    }
  });
})();
